import type React from 'react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import type {
  ButtonProps,
  ButtonSizes,
  ButtonVariants,
} from '@xing-com/button';
import { Button } from '@xing-com/button';
import type { ApplicationData } from '@xing-com/crate-jobs-helpers';
import {
  useApplicationStatusHelpers,
  useApplicationTypeHelpers,
} from '@xing-com/crate-jobs-hooks';
import { IconApply, IconTick } from '@xing-com/icons';

import { useApplyButtonStateProps } from './apply-button-state-context';
import { useApplyButtonDefaultProps } from './use-apply-button-default-props';
import { useApplyButtonEmailApplyProps } from './use-apply-button-email-apply-props';
import { useApplyButtonMessageApplyProps } from './use-apply-button-message-apply-props';
import { useApplyButtonUrlApplyProps } from './use-apply-button-url-apply-props';
import type { ApplyTrackingProps } from './use-track-apply-intention';

type Props = {
  application: ApplicationData;
  size?: ButtonSizes;
  variant?: ButtonVariants;
  icon?: ButtonProps['icon'];
  iconOnly?: boolean;
  className?: string;
  trackingProps?: ApplyTrackingProps;
};

const Apply = styled(Button)`
  white-space: nowrap;
`;

export const ApplyButton: React.FC<Props> = ({
  application,
  size = 'medium',
  variant = 'primary',
  icon,
  iconOnly,
  className,
  trackingProps,
}) => {
  const intl = useIntl();
  const { appliedAt } = useApplicationStatusHelpers(application);

  const {
    type,
    applyUrl,
    applyEmail,
    contactUserId,
    code,
    jobId,
    prioritized,
    slug,
    title,
  } = application;
  const hasApplied = !!appliedAt;

  const {
    isXingApplication,
    isCustomApplication,
    isEmailApplication,
    isMessageApplication,
    isUrlApplication,
  } = useApplicationTypeHelpers(type);

  const label = useMemo(() => {
    if (hasApplied) {
      return 'JOBS_MYJOBS_APPLIED_STATUS';
    }
    if (isXingApplication || isCustomApplication) {
      return 'JOBS_JOB_DETAILS_XING_APPLY';
    }
    if (isUrlApplication) {
      return 'JOB_POSTING_APPLY_EXTERNAL_URL';
    }
    return 'JOBS_JOB_DETAILS_APPLY';
  }, [hasApplied, isXingApplication, isCustomApplication, isUrlApplication]);

  const defaultProps = useApplyButtonDefaultProps(slug);
  const applyFlowProps = useApplyButtonStateProps();
  const messageApplyProps = useApplyButtonMessageApplyProps({
    contactUserId,
    jobId,
    trackingProps,
  });
  const urlApplyProps = useApplyButtonUrlApplyProps({
    jobId,
    slug,
    applyUrl,
    isPrioritizedJob: prioritized,
    trackingProps,
  });
  const emailApplyProps = useApplyButtonEmailApplyProps({
    code,
    applyEmail,
    title,
    jobId,
    trackingProps,
  });
  const additionalProps = {
    ...defaultProps,
    ...(isEmailApplication && emailApplyProps),
    ...(isMessageApplication && messageApplyProps),
    ...(isUrlApplication && urlApplyProps),
    ...((isXingApplication || isCustomApplication) && applyFlowProps),
  };

  const handleOnClick = (event: React.MouseEvent): void => {
    event.stopPropagation();

    if (isXingApplication || isCustomApplication) {
      applyFlowProps.onClick(trackingProps);
    } else {
      additionalProps.onClick?.();
    }
  };

  const getIcon = (): ButtonProps['icon'] => {
    if (iconOnly) return IconApply;
    if (hasApplied) return IconTick;
    return icon ?? additionalProps.icon;
  };

  return (
    <Apply
      disabled={hasApplied}
      variant={variant}
      size={size}
      aria-label={intl.formatMessage({ id: label })}
      className={className}
      {...additionalProps}
      icon={getIcon()}
      onClick={handleOnClick}
    >
      {!iconOnly && <FormattedMessage id={label} />}
    </Apply>
  );
};
